import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FocusHeader } from '../components/FocusHeader';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { StepIndicator } from '../components/StepIndicator';
import { useSession } from '../contexts/SessionContext';
import { supabase } from '../lib/supabase';
import { getWizardSteps, useWizardNavigation } from '../lib/wizardSteps';
import { Sparkles, PenLine, AlertTriangle } from 'lucide-react';

interface RejectionReason {
  id: string;
  reason_text: string;
  display_order: number;
}

interface InterfaceTexts {
  reject_title: string;
  reject_subtitle: string;
  reject_reason_prompt: string;
  reject_custom_placeholder: string;
  reject_next_step: string;
}

export function IdeaReject() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, user, currentIdea, refreshSession } = useSession();
  const [rejectionReason, setRejectionReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [showWarning, setShowWarning] = useState(false);
  const [rejectedCount, setRejectedCount] = useState(0);
  const [reasons, setReasons] = useState<RejectionReason[]>([]);
  const [interfaceTexts, setInterfaceTexts] = useState<InterfaceTexts>({
    reject_title: 'Waarom past dit idee niet?',
    reject_subtitle: 'Dit helpt ons om betere ideeën te genereren',
    reject_reason_prompt: 'Kies een reden of type je eigen reden:',
    reject_custom_placeholder: 'Of type je eigen reden...',
    reject_next_step: 'Hoe wil je verder?',
  });
  const [dashboardSession, setDashboardSession] = useState<any>(null);
  const [dashboardUser, setDashboardUser] = useState<any>(null);
  const [dashboardIdea, setDashboardIdea] = useState<any>(null);

  useEffect(() => {
    const sessionId = location.state?.sessionId;

    if (sessionId) {
      // Load from dashboard/navigation
      loadDashboardData(sessionId);
    } else if (session && user && currentIdea) {
      // Load from SessionContext
      loadData();
    } else {
      setDataLoading(false);
    }
  }, [location.state?.sessionId, session, user, currentIdea]);

  const loadDashboardData = async (sessionId: string) => {
    setDataLoading(true);

    try {
      console.log('[IdeaReject] Loading session by ID:', sessionId);

      // Load session
      const { data: sessionData, error: sessionError } = await supabase
        .from('sessions')
        .select('*')
        .eq('id', sessionId)
        .maybeSingle();

      if (sessionError || !sessionData) {
        console.error('[IdeaReject] Error loading session:', sessionError);
        setDataLoading(false);
        return;
      }

      setDashboardSession(sessionData);

      // Load user
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', sessionData.user_id)
        .maybeSingle();

      if (userError || !userData) {
        console.error('[IdeaReject] Error loading user:', userError);
        setDataLoading(false);
        return;
      }

      setDashboardUser(userData);

      // Load the current idea (the one to be rejected)
      const { data: ideaData, error: ideaError } = await supabase
        .from('blog_ideas')
        .select('*')
        .eq('session_id', sessionId)
        .eq('status', 'presented')
        .order('presented_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (ideaError || !ideaData) {
        console.error('[IdeaReject] Error loading idea:', ideaError);
        setDataLoading(false);
        return;
      }

      console.log('[IdeaReject] Idea found:', ideaData.idea_title);
      setDashboardIdea(ideaData);

      // Load rejection reasons and interface texts
      await loadStaticData(sessionId);
    } catch (error) {
      console.error('[IdeaReject] Exception loading dashboard data:', error);
      setDataLoading(false);
    }
  };

  const loadData = async () => {
    if (!session) return;

    setDataLoading(true);
    await loadStaticData(session.id);
  };

  const loadStaticData = async (sessionId: string) => {
    try {

      // Load rejection reasons
      const { data: reasonsData } = await supabase
        .from('rejection_reasons')
        .select('id, reason_text, display_order')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (reasonsData) {
        setReasons(reasonsData);
      }

      // Load interface texts
      const { data: textsData } = await supabase
        .from('interface_texts')
        .select('text_key, text_content');

      if (textsData) {
        const texts: any = {};
        textsData.forEach((item) => {
          texts[item.text_key] = item.text_content;
        });
        setInterfaceTexts(texts);
      }

      // Count rejected ideas
      const { count } = await supabase
        .from('blog_ideas')
        .select('*', { count: 'exact', head: true })
        .eq('session_id', sessionId)
        .eq('status', 'rejected');

      if (count && count >= 7) {
        setShowWarning(true);
        setRejectedCount(count);
      }

      setDataLoading(false);
    } catch (error) {
      console.error('[IdeaReject] Error loading static data:', error);
      setDataLoading(false);
    }
  };

  const activeSession = dashboardSession || session;
  const { handleStepClick } = useWizardNavigation(activeSession);
  const activeUser = dashboardUser || user;
  const activeIdea = dashboardIdea || currentIdea;

  if (loading || dataLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <FocusHeader
          title="Idee afwijzen"
          onBack={() => navigate('/dashboard')}
        />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <LoadingSpinner text={loading ? "We maken een nieuw idee voor je..." : "Laden..."} />
        </main>
      </div>
    );
  }

  if (!activeSession || !activeUser || !activeIdea) {
    return (
      <div className="min-h-screen bg-gray-50">
        <FocusHeader
          title="Idee afwijzen"
          onBack={() => navigate('/dashboard')}
        />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="card bg-red-50 border-red-200">
            <h2 className="text-xl font-bold text-red-800 mb-2">Geen blog idee gevonden</h2>
            <p className="text-red-700 mb-4">Er is geen actief idee om af te wijzen.</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="btn-secondary"
            >
              Terug naar Dashboard
            </button>
          </div>
        </main>
      </div>
    );
  }

  const handleNewAIIdea = async () => {
    if (!rejectionReason.trim()) {
      alert('Geef eerst aan waarom dit idee niet past.');
      return;
    }

    setLoading(true);

    try {
      // First, fetch all previously rejected ideas (before marking current one)
      const { data: previouslyRejectedIdeas, error: fetchError } = await supabase
        .from('blog_ideas')
        .select('idea_title, rejection_reason')
        .eq('session_id', activeSession.id)
        .eq('status', 'rejected')
        .order('presented_at', { ascending: true });

      if (fetchError) {
        console.error('Error fetching rejected ideas:', fetchError);
        // Continue anyway - we can still generate a new idea without previous context
      }

      console.log('Previously rejected ideas:', previouslyRejectedIdeas);

      // Now mark current idea as rejected
      const { error: updateError } = await supabase
        .from('blog_ideas')
        .update({
          status: 'rejected',
          rejection_reason: rejectionReason,
          decided_at: new Date().toISOString(),
        })
        .eq('id', activeIdea.id);

      if (updateError) {
        console.error('Error updating idea:', updateError);
        throw new Error('Kon idee niet afwijzen');
      }

      // Build context including all rejected ideas (previous + current)
      const allRejectedIdeas = [
        ...(Array.isArray(previouslyRejectedIdeas) ? previouslyRejectedIdeas : []),
        {
          idea_title: activeIdea.idea_title,
          rejection_reason: rejectionReason,
        },
      ];

      const previousTopicsContext = allRejectedIdeas.length > 0
        ? allRejectedIdeas
            .map((idea, index) =>
              `${index + 1}. "${idea.idea_title}" - ${idea.rejection_reason || 'Geen reden opgegeven'}`
            )
            .join('\n')
        : `1. "${activeIdea.idea_title}" - ${rejectionReason}`;

      console.log('Sending to AI:', { previousTopicsContext });

      const { data: { session } } = await supabase.auth.getSession();
      const authToken = session?.access_token || (import.meta.env.VITE_SUPABASE_ANON_KEY);

      // Call OpenAI to generate new idea
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/openai-generate`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            type: 'idea',
            session_id: activeSession.id,
            user_id: activeUser.id,
            data: {
              previous_topics: previousTopicsContext,
            },
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        console.error('OpenAI API error:', data);
        throw new Error(data.error || 'AI kon geen idee genereren');
      }

      console.log('AI response:', data);

      let ideaData;
      if (data.success && data.data) {
        if (typeof data.data === 'string') {
          ideaData = JSON.parse(data.data);
        } else {
          ideaData = data.data;
        }
      } else if (data.result) {
        ideaData = data.result;
      } else {
        throw new Error('Onverwachte response structuur van AI');
      }

      // Create new blog idea
      const { error: insertError } = await supabase.from('blog_ideas').insert({
        session_id: activeSession.id,
        user_id: activeUser.id,
        source: 'ai_regenerated',
        idea_title: ideaData.title,
        idea_content: ideaData.content,
        idea_reason: ideaData.reason || '',
        status: 'presented',
      });

      if (insertError) {
        console.error('Error inserting new idea:', insertError);
        throw new Error('Kon nieuw idee niet opslaan');
      }

      console.log('New idea created, updating session state to idea_selection...');

      // Update session state back to idea_selection so the new idea is shown
      const { error: sessionUpdateError } = await supabase
        .from('sessions')
        .update({
          state: 'idea_selection',
          approved_idea_id: null  // Clear any previous approved idea
        })
        .eq('id', activeSession.id);

      if (sessionUpdateError) {
        console.error('Error updating session state:', sessionUpdateError);
        throw new Error('Kon sessie niet bijwerken');
      }

      console.log('Session state updated, refreshing session context...');

      // Refresh session to load new idea (if available)
      if (refreshSession) {
        await refreshSession();
      }

      // Wait a bit longer to ensure state is fully updated
      await new Promise(resolve => setTimeout(resolve, 300));

      console.log('Navigating to idea check with new idea...');

      // Navigate back to idea check with sessionId
      navigate('/idea-check', {
        state: { sessionId: activeSession.id }
      });
    } catch (error: any) {
      console.error('Error generating new idea:', error);
      const errorMessage = error.message || 'Er ging iets mis bij het genereren van een nieuw idee. Probeer het opnieuw.';
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomIdea = async () => {
    if (!rejectionReason.trim()) {
      alert('Geef eerst aan waarom dit idee niet past.');
      return;
    }

    try {
      // Mark current idea as rejected
      await supabase
        .from('blog_ideas')
        .update({
          status: 'rejected',
          rejection_reason: rejectionReason,
          decided_at: new Date().toISOString(),
        })
        .eq('id', activeIdea.id);

      // Navigate to custom idea input
      navigate('/idea-custom', {
        state: { sessionId: activeSession.id }
      });
    } catch (error) {
      console.error('Error rejecting idea:', error);
    }
  };

  const steps = getWizardSteps(activeSession, 1);

  return (
    <div className="min-h-screen bg-gray-50">
      <FocusHeader
        title="Idee afwijzen"
        onBack={() => navigate('/dashboard')}
      />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <StepIndicator steps={steps} onStepClick={handleStepClick} />
        <div className="space-y-6 mt-6">
          <div className="space-y-2">
            <h1 className="text-2xl md:text-3xl font-bold text-[#231F20]">
              {interfaceTexts.reject_title}
            </h1>
            <p className="text-gray-600">{interfaceTexts.reject_subtitle}</p>
          </div>

          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700">{interfaceTexts.reject_reason_prompt}</p>
            <div className="flex flex-wrap gap-2">
              {reasons.map((reason) => (
                <button
                  key={reason.id}
                  onClick={() => setRejectionReason(reason.reason_text)}
                  className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                    rejectionReason === reason.reason_text
                      ? 'bg-[#231F20] text-white border-[#231F20]'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                  }`}
                >
                  {reason.reason_text}
                </button>
              ))}
            </div>
            <textarea
              className="textarea-field min-h-[100px]"
              placeholder={interfaceTexts.reject_custom_placeholder}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            />
          </div>

          {showWarning && (
            <div className="card bg-yellow-50 border-yellow-200">
              <div className="flex gap-3">
                <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
                <div className="space-y-2">
                  <p className="font-semibold text-yellow-900">
                    Je hebt al {rejectedCount} ideeën geprobeerd
                  </p>
                  <p className="text-sm text-yellow-800">
                    Geen goed idee gevonden? Probeer een eigen idee in te voeren, of laat het ons
                    weten via{' '}
                    <a href="mailto:dibbi@uniekwerk.nl" className="underline font-medium">
                      dibbi@uniekwerk.nl
                    </a>
                  </p>
                  <p className="text-sm text-yellow-700">Je kunt wel doorgaan met nieuwe ideeën.</p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <p className="font-semibold text-[#231F20]">{interfaceTexts.reject_next_step}</p>

            <button
              onClick={handleNewAIIdea}
              disabled={!rejectionReason.trim()}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Sparkles className="w-5 h-5" />
              Laat AI een nieuw idee maken
            </button>

            <button
              onClick={handleCustomIdea}
              disabled={!rejectionReason.trim()}
              className="btn-secondary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PenLine className="w-5 h-5" />
              Ik heb een eigen idee
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
